import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Label, Modal, Popup } from 'semantic-ui-react';
import { useMessageResources, useTextResources } from '../../language';
import LanguagesDropDown from '../../components/shared/LanguagesDropDown';
import GridPanel from '../../components/GridPanel';
import { getMissingFieldsFromObject, groupByIntoArray } from '../../components/shared/utils';
import { applySavedTranslations, belongsToDeletedMapping } from './contentTranslations';
import { onError, onWarning, onSuccess } from '../../components/shared/messages';
import Panel from '../../components/shared/Panel';
import { Addons, GenericFields, Hotels, Packages, Promotions, Rates, ReservationPolicy, RoomTypes, PriceLists, PackageGroup } from '../../api/services/next';
import { EntityType, MenuActions, contentDescriptionLength } from '../../components/shared/constants';
import GenericFieldNamesDropDown from '../../components/shared/GenericFieldNamesDropDown';
import GenericFieldValuesDropDown from '../../components/shared/GenericFieldValuesDropDown';
import GenericMultipleFieldValuesDropDown from '../../components/shared/GenericMultipleFieldValuesDropDown';
import { useCurrentHotel } from '../../hooks/useCurrentHotel';
import EntityTypesDropDown from '../../components/shared/EntityTypesDropDown';
import { useMemo, useContext } from 'react';
import { PriceListContext } from './context';
import DropDown from '../../components/shared/dropdown';
import TextInputWithHtmlEditor from '../../components/shared/TextInputWithHtmlEditor';
import { useConfirm } from "../../hooks/useConfirm";
import { PermissionsActions, UserHasPermission } from '../../components/shared/permissionsActions';
import { useLoader } from '../../hooks/useLoader';

const genericFieldAndValueDropDownStyles = { position: 'absolute', marginTop: '-17px', width: '13%' };
const defaultInputProps = { search: true, style: genericFieldAndValueDropDownStyles };
const genericFieldNameDropDownDuplicatedProps = { ...defaultInputProps, disabled: !UserHasPermission(PermissionsActions.EditFieldName), allowAdditions: UserHasPermission(PermissionsActions.AddFieldName) };
const genericFieldValueDropDownDuplicatedProps = { ...defaultInputProps, disabled: !UserHasPermission(PermissionsActions.EditFieldValue), allowAdditions: UserHasPermission(PermissionsActions.AddFieldValue) };
const genericFieldNameCompareFunction = (a, b) => {
    if (a.IsSystem && !b.IsSystem) {
        return -1;
    }

    if (!a.IsSystem && b.IsSystem) {
        return 1;
    }

    return a.FieldName.toLowerCase() < b.FieldName.toLowerCase() ? -1 : 1
};

let urlParams = new URLSearchParams(location.search);
let urlEntityTypeId = urlParams.get("EntityTypeId") || urlParams.get("entityTypeId");
if(urlEntityTypeId)
{
    urlEntityTypeId = parseInt(urlEntityTypeId);
}
let urlEntityId = urlParams.get("EntityId") || urlParams.get("entityId");
if(urlEntityId)
{
    urlEntityId = parseInt(urlEntityId);
}
let urlHotelId = urlParams.get("HotelId") || urlParams.get("hotelId");
if(urlHotelId)
{
    urlHotelId = parseInt(urlHotelId);
}
const userLanguageId = window.optima?.user?.UserSettings?.[0].LanguageID || null;

export const Content = ({ EntityTypeId, EntityId, HotelId }) => {
    const message = useMessageResources();
    const { state } = useContext(PriceListContext);
    const currentHotel = useCurrentHotel() || {};
    const { HotelChainId: currentHotelChainId, HotelParameter: { LanguageID: defaultLanguageId } = {} } = currentHotel;
    const [genericFieldTranslations, setGenericFieldTranslations] = useState([]);
    const [mappingIdsForDeletion, setMappingIdsForDeletion] = useState([]);
    const [translationForDeletion, setTranslationForDeletion] = useState([]);
    const [genericFieldMappings, setGenericFieldMappings] = useState([]);
    const [languageId, setLanguageId] = useState(defaultLanguageId || userLanguageId);
    const [genericFieldNames, setGenericFieldNames] = useState([]);
    const [genericFieldValues, setGenericFieldValues] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [entityTypeId, setEntityTypeId] = useState(EntityTypeId || urlEntityTypeId);
    const [entityId, setEntityId] = useState(EntityId || urlEntityId);
    const [entities, setEntities] = useState({});
    const [allHotelsOfChain, setAllHotelsOfChain] = useState([]);
    const [hotelIds, setHotelIds] = useState([]);
    const loader = useLoader();
    const text = useTextResources();
    const confirm = useConfirm();
    const apis = useMemo(() => [
        {
            api: Rates.GetRates,
            EntityTypeId: EntityType.Rate
        },
        {
            api: Addons.GetHotelChainAddons,
            EntityTypeId: EntityType.AddOn
        },
        {
            api: Packages.GetPackages,
            EntityTypeId: EntityType.Package
        },
        {
            api: PackageGroup.GetPackageGroups,
            EntityTypeId: EntityType.PackageGroup,
            skipHotelChainFilter: true,
            // Enhanced map: fill the Name column with Description value.
            map: (res = []) => {
                const list = Array.isArray(res) ? res : res.Data || res.Items || [];
                return list.map(packageGroup => ({
	                    ...packageGroup,
	                    Id: packageGroup.Id,
	                    Name: packageGroup.Description
	                }));
                }
        },
        {
            api: RoomTypes.GetRoomTypes,
            EntityTypeId: EntityType.RoomType
        },
        {
            api: (spec) => Promotions.GetPromotions({ ...spec, MultiRateDiscountsOnly: true }),
            EntityTypeId: EntityType.Promotion
        },
        {
            api: Hotels.GetHotels,
            EntityTypeId: EntityType.Hotel,
        },
        {
            api: ReservationPolicy.GetReservationPolicies,
            EntityTypeId: EntityType.ReservationPolicy
        },
        {
            api: PriceLists.GetPriceGroups,
            EntityTypeId: EntityType.PriceGroup,
        },
        {
            api: PriceLists.GetClassificationCategories,
            EntityTypeId: EntityType.ClassificationCategories,
        }
    ], []);

    const changeLanguageHandler = useCallback(async (languageId) => {

        if (typeof languageId === 'string' || languageId === null) { //Means no languageId was chosen
            return;
        }

        // Check if translations for the languageId already exist
        if (genericFieldTranslations[languageId]) {
            // If they exist, no need to do anything else - it will use the existing ones and not refetch
            return;
        }

        try {
            const translations = await GenericFields.GetGenericFieldsTranslations({
                LanguageId: languageId,
            });

            // Update state with new translations under new languageId property
            setGenericFieldTranslations((prev) => ({
                    ...prev,
                    [languageId]: translations
                }));
        } catch (error) {
            console.error('Error fetching translations:', error);
            onWarning(error);
        }
    }, [genericFieldTranslations]);

    useEffect(() => {
        (async () => {
            try {
                setAllHotelsOfChain(await Hotels.GetHotels({
                    IsActive: true,
                    IncludeOnlyHotelsWithUserPermissions: true
                }));

                if(urlHotelId)
                {
                    setHotelIds([urlHotelId]);
                }
            } catch (error) {
                onError(error);
                console.error(error);
            }
        })();
    }, [])

    // Fetch translations once per language
    useEffect(() => {
        (async () => {
                await changeLanguageHandler(languageId);
        })();
    }, [languageId, changeLanguageHandler]);

    const updateAndMergeTranslations = useCallback(async (currentTranslations, languageId, row, property, value) => {

        const updatedTranslations = { ...currentTranslations };
        const otherProperty = property === "ShortValue" ? "LongValue" : "ShortValue";

        const translationIndex = updatedTranslations[languageId]?.findIndex(
            (t) => row?.ValueId ? t.ValueId == row?.ValueId : t.MappingId == row.Id
        );
        const currentTranslation = updatedTranslations[languageId][translationIndex]
        //If translation is not on DB and after this function call both the long and short descriptions will be empty, remove it.
        if (!value.toString().trim() && !currentTranslation?.Id) {
            const otherProperty = property === "ShortValue" ? "LongValue" : "ShortValue";
            const hasOtherValue = Boolean(currentTranslation?.[otherProperty]);

            // Check if the other value (not being currently set) is also empty.
            if (!hasOtherValue) {
                const newUpdatedTranslations = removeIndex(updatedTranslations, languageId, translationIndex);
                return newUpdatedTranslations;
            }
        }
        //If it's a newly created translations, add to dictionary, else update the specific one
        if (currentTranslation) {
            currentTranslation[property] = value;
            currentTranslation.updated = true;
        } else {
            updatedTranslations[languageId] = [
                ...(updatedTranslations[languageId]),
                {
                    ValueId: row.ValueId,
                    [property]: value,
                    [otherProperty]: "",
                    LanguageId: languageId,
                    MappingId: row.Id,
                    updated: true
                },
            ];
        }

        return updatedTranslations;
    }, []);

    const contentTabSaveHandler = useCallback(async (flatTranslations) => {
        const newItems = [];
        const existingItems = [];

        for (const map of genericFieldMappings) {
            // If the Id property is of type string, create a new object with Id set to 0.
            // Otherwise, use the item as it is.
            let item = (typeof map.Id === 'string') ? { ...map, Id: 0 } : map;

            if (!map.ValueId) {
                const newFieldValueFromServer = await GenericFields.PostGenericFieldsValues({ Id: 0, value: '', FieldId: map.FieldId });
                item = { ...item, ValueId: newFieldValueFromServer.Id };

                flatTranslations.filter(ft => ft.MappingId == map.Id).map(ft =>
                    ft['ValueId'] = newFieldValueFromServer.Id)
            }

            if (item.Id === 0) {
                newItems.push(item);
            } else {
                existingItems.push(item);
            }
        }

        // Create the reordered list- new items are reversed so that after save, order in the UI remains the same.
        const reorderedList = [...newItems.reverse(), ...existingItems];

        // Deletion has to happen prior to the saving to avoid possible problems with new created rows' unique constraint.
        if (mappingIdsForDeletion.length) {
            await GenericFields.DeleteGenericFieldsMappings(mappingIdsForDeletion);
            setMappingIdsForDeletion([])
        }

        const deletedMappingIds = translationForDeletion;

        if (deletedMappingIds.length) {
            const isDeleted = belongsToDeletedMapping(deletedMappingIds);
            flatTranslations = flatTranslations.filter(t => !isDeleted(t));
            setTranslationForDeletion([])
        }
        // Now we can run the other two in parallel
        const [savedMappings, savedTranslations] = await Promise.all([
            genericFieldMappings.length ? GenericFields.SaveGenericFieldsMapping(reorderedList) : null,
            flatTranslations.length ? GenericFields.SaveGenericFieldTranslations(flatTranslations) : null,
        ]);

        if (savedMappings) {
            setGenericFieldMappings(savedMappings);
        }

        setGenericFieldTranslations(prev => applySavedTranslations(prev, savedTranslations, deletedMappingIds));
    }, [
        genericFieldMappings,
        mappingIdsForDeletion,
        translationForDeletion,
        setMappingIdsForDeletion,
        setTranslationForDeletion,
        setGenericFieldMappings,
        setGenericFieldTranslations,
    ]);

    //Takes and holds the translations from the dictionary by the current chosen languageId
    const derivedTranslationsForSelectedLanguage = languageId ? genericFieldTranslations[languageId] : [];
    const save = useCallback(async () => {
        const validate = (genericFieldsMappings) => {
            for (let genericFieldMapping of genericFieldsMappings) {

                let missingFieldsGenericFields = getMissingFieldsFromObject(genericFieldMapping, ["FieldId", "EntityId"]);

                const isFieldIdMissing = missingFieldsGenericFields.includes("FieldId")
                const isEntityIdMissing = missingFieldsGenericFields.includes("EntityId")

                if (isFieldIdMissing) {
                    onWarning(message('OneOrMoreRowsHasAMissingFieldName'));
                     return false;
                }

                if (isEntityIdMissing) {
                    onWarning(message('OneOrMoreRowsHasAMissingEntity'));
                     return false;
                }
            }
            return true;
        };

        try {
            loader(true);
            const flatTranslations = Object.values(genericFieldTranslations)
                .filter(valuesArray => valuesArray.length)
                .flat()
                .filter(({ updated }) => updated);
            const isMappingAndTranslationsValid = validate(genericFieldMappings, flatTranslations);

            if (!isMappingAndTranslationsValid) {
                return;
            }
            await contentTabSaveHandler(flatTranslations);
            onSuccess();
        } catch (e) {
            onError(e?.error?.detail ? e?.error?.detail : e);
        } finally {
            loader(false);
        }
    }, [genericFieldMappings, genericFieldTranslations, contentTabSaveHandler, loader, message]);

    useEffect(() => {
        if (window.optima?.commands) {
            window.optima.commands.unsubscribe(MenuActions.save, save);
            window.optima.commands.subscribe(MenuActions.save, save);
        }
    }, [state, genericFieldTranslations, genericFieldMappings, languageId, genericFieldNames, genericFieldValues, save]);

    useEffect(() => {
        (async () => {
            try {
                loader(true);
                let tasks = [];
                tasks.push(GenericFields.GetGenericFields());
                tasks.push(GenericFields.GetGenericFieldsValues());
                tasks.push(GenericFields.GetGenericFieldsMappings({
                    EntityTypeId: entityTypeId,
                    EntityId: entityId
                }));
                const [genericFieldsFromServer, genericFieldValuesFromServer, genericFieldsMappingsFromServer] = await Promise.all(tasks)

                setGenericFieldNames(genericFieldsFromServer);
                setGenericFieldValues(genericFieldValuesFromServer);
                setGenericFieldMappings(genericFieldsMappingsFromServer);
            } catch (error) {
                onError(error);
                console.error(error);
            } finally {
                loader(false);
            }
        })();
    }, [entityId, entityTypeId, loader]);

    useEffect(() => {
        if (EntityTypeId)
            setEntityTypeId(EntityTypeId);
        if (EntityId)
            setEntityId(EntityId);
    }, [EntityTypeId, EntityId])

    useEffect(() => {
        (async () => {

            loader(true);
            try {
                const dataPatch = {};
                // Load only when either hotels loaded (for hotel-dependent APIs) or there are no hotel-dependent APIs.
                const hasHotelDependent = apis.some(a => !a.skipHotelChainFilter);
                if (hasHotelDependent && allHotelsOfChain.length === 0) {
                    loader(false);
                    return;
                }
                const relatedApis = apis;

                const tasks = relatedApis.map(a =>
                    a.skipHotelChainFilter
                        ? a.api({})
                        : a.api({
                            HotelIds: hotelIds.length === 0 ? allHotelsOfChain.map(h => h.Id) : hotelIds,
                            HotelChainId: currentHotelChainId
                        })
                );

                const results = await Promise.all(tasks);
                results.forEach((res, i) => {
                    const def = relatedApis[i];
                    dataPatch[def.EntityTypeId] = def.map ? def.map(res) : res;
                });

                // Replace entire entities state to keep previous business logic (no stale cached entity types)
                setEntities(dataPatch);
            } catch (error) {
                console.error('Error fetching data:', error);
                onWarning(error);
            } finally {
                loader(false);
            }
        })();
    }, [allHotelsOfChain, hotelIds, apis, currentHotelChainId]);

    const confirmDeletion = useCallback(() => confirm({
        header: message('AreYouSure'),
        content: message('AreYouSureYouWouldLikeToDeleteSelectedItems'),
        confirmButton: {
            content: text('Yes'),
            positive: true,
        },
        cancelButton: {
            content: text('No'),
            negative: true,
        },
    }), [confirm, message, text]);

    const onGridUpdate = async (added, changed, deleted) => {
        let updatedToSave = [...genericFieldMappings];

        if (deleted?.length > 0 && await confirmDeletion()) {

            let mappingsToDeleteFromServer = deleted.map(d => genericFieldsData[d]);

            let additionalMappingsToDelete = [];
            mappingsToDeleteFromServer.forEach((mapping) => {
                if (multipleValuesFieldsIds.includes(mapping.FieldId)) {
                    additionalMappingsToDelete = [...additionalMappingsToDelete, ...mapping.ValueId];
                }
            });

            const mappingsIdsToDeleteFromServer = mappingsToDeleteFromServer.concat(additionalMappingsToDelete).map(m => m.Id);
            const mappingsToDeleteWithoutNewly = mappingsIdsToDeleteFromServer.filter(m => typeof m !== 'string');
            setTranslationForDeletion([...mappingsIdsToDeleteFromServer.filter(m => typeof m === 'string'), ...translationForDeletion])

            // Attempt to delete the specified mappings from the server
            if (mappingsToDeleteWithoutNewly.length > 0) {
                setMappingIdsForDeletion([...mappingsToDeleteWithoutNewly, ...mappingIdsForDeletion])
            }
            // Remove all deleted items from the state (both new and non-new IDs)          
            updatedToSave = updatedToSave.filter(({ Id }) => !mappingsIdsToDeleteFromServer.includes(Id));
        }

        setGenericFieldMappings(updatedToSave);
    };

    const editFieldWithDropDown = (property, newValue, mappingId) => {
        setGenericFieldMappings(prev => prev.map(mapping =>
            mapping.Id !== mappingId ?
                mapping :
                { ...mapping, [property]: newValue }))
    };

    const createNewMapping = () => {
        return {
            Id: `newMappingId-${crypto.randomUUID()}`,
            HotelChainId: currentHotelChainId,
            EntityId: entityId,
            EntityTypeId: entityTypeId,
        }
    }

    const editableFieldNameFormatter = ({ value, row }) => {
        if (!row) return genericFieldNames?.find(x => x.Id === value)?.FieldName || "";

        const onAddItem =
            async (e, { value }) => {
                const newFieldFromServer = await GenericFields.PostGenericFields({ Id: 0, FieldName: value })
                setGenericFieldNames([newFieldFromServer, ...genericFieldNames])
                onChange(newFieldFromServer.Id)
            }

        const onChange = (value) => {
            if (multipleValuesFieldsIds.includes(row.FieldId)) {
                handleMultipleValuesField(value);
            }
            else {
                handleSingleValueField(value);
            }
        }

        const handleMultipleValuesField = (value) => {
            // If old value is multiple value field
            // add mappings for deletion on server side and remove them from state
            // keep last mapping with new FieldId
            const itemsToDelete = row.ValueId;
            const indexOfLastElement = itemsToDelete.length - 1;
            const mappingToKeep = row.ValueId[indexOfLastElement] ?? row;
            setMappingIdsForDeletion(prev => [
                ...prev,
                ...itemsToDelete
                    .filter(mapping => typeof mapping.Id !== 'string' && mapping.Id !== mappingToKeep.Id)
                    .map(x => x.Id)
            ]
            );

            setGenericFieldMappings(prev => {
                const itemsToDeleteWithoutLastItem = prev.filter(mapping =>
                    !itemsToDelete.some(dm => dm.Id === mapping.Id && dm.Id !== mappingToKeep.Id));

                return itemsToDeleteWithoutLastItem.map(mapping =>
                    mapping.Id !== mappingToKeep.Id ? mapping : { ...mapping, ValueId: null, FieldId: value }
                );
            });
        }

        const handleSingleValueField = (value) => {
            editFieldWithDropDown('FieldId', value, row.Id);
        }

        // Filter out fieldNames that are already used in other rows except for the current one
        let filteredFieldNames = genericFieldNames
            .filter(gfn =>
                gfn.Id === row.FieldId ||
                (
                    (
                        !row.EntityId ||
                        !genericFieldMappings.find(x =>
                            x.EntityTypeId === row.EntityTypeId &&
                            x.EntityId === row.EntityId &&
                            x.FieldId === gfn.Id)
                    )
                    &&
                    (
                        !gfn.GenericFieldsMappings ||
                        gfn.GenericFieldsMappings?.find(x => x.EntityTypeId === row.EntityTypeId) ||
                        gfn.IsSystem
                    )
                )
            )
            .sort(genericFieldNameCompareFunction);

        let isSystemGenericFieldSelected = filteredFieldNames.find(x => x.Id === value)?.IsSystem;
        return <GenericFieldNamesDropDown
            {...genericFieldNameDropDownDuplicatedProps}
            className={isSystemGenericFieldSelected ? 'system-field-selected' : ''}
            value={value}
            data={filteredFieldNames}
            onChange={onChange}
            onAddItem={onAddItem}
            placeholder={text('ChooseFieldName')}
            labelField={systemGenericFieldIndicator}
        />
    }

    const systemGenericFieldIndicator = useCallback(item => {
        return item.IsSystem ? <Popup trigger={<Label circular color="green" empty />}
            position="left center"
            basic
            content={text("SystemGenericField")}
        ></Popup> : undefined;
    }, [text]);


    const onChangeGenericValue = (value, row) => {
        editFieldWithDropDown('ValueId', value, row.Id)
    }

    const onAddGenericFieldValue = async (value, row) => {
        const newFieldFromServer = await GenericFields.PostGenericFieldsValues({ Id: 0, value: value, FieldId: row.FieldId })
        setGenericFieldValues([newFieldFromServer, ...genericFieldValues]);
        genericFieldNames.find(gf => gf.Id === row.FieldId)?.GenericFieldsValues?.push(newFieldFromServer);
        setGenericFieldNames(genericFieldNames);
        onChangeGenericValue(newFieldFromServer.Id, row);
    }

    const editableFieldValueFormatter = ({ value, row }) => {
        if (value?.length) {
            const mappingIds = value.map(x => x.ValueId);
            const data = genericFieldValues.filter(gfv => gfv.FieldId === row.FieldId && gfv.Value);
            const addMappingWithNewValue = (newValueId) => {
                const newMapping = {
                    ...row,
                    Id: `newMappingId-${crypto.randomUUID()}`,
                    ValueId: newValueId
                };

                setGenericFieldMappings(prev => [newMapping, ...prev]);
            };

            const onAddItem = async (e, { value }) => {
                const existingValues = genericFieldValues.filter(gv => gv.FieldId === row.FieldId);
                if (existingValues.find(gv => gv.Value.toUpperCase() === value.toUpperCase())) {
                    onWarning(message('FieldValueCannotBeDuplicate'))
                    return;
                }
                const newFieldFromServer = await GenericFields.PostGenericFieldsValues({ Id: 0, value: value, FieldId: row.FieldId })
                setGenericFieldValues([newFieldFromServer, ...genericFieldValues]);
                genericFieldNames.find(gf => gf.Id === row.FieldId)?.GenericFieldsValues?.push(newFieldFromServer);
                setGenericFieldNames(genericFieldNames);
                addMappingWithNewValue(newFieldFromServer.Id);
            }

            const onChange = (value) => {
                if (row.ValueId.length > value.length) {
                    const indexOfLastItemBeforeChange = row.ValueId.length - 1
                    const mappingToKeep = row.ValueId[indexOfLastItemBeforeChange];
                    const mappingToKeepId = mappingToKeep?.Id;
                    const isTryingToRemoveInitialElement = !value.includes(mappingToKeep?.ValueId);
                    let newValueForMappingToKeep = null;

                    if (value.length !== 0 && isTryingToRemoveInitialElement) {
                        const indexOfLastItemInAfterChange = value.length - 1;
                        newValueForMappingToKeep = row.ValueId.find(gm => gm.ValueId === value[indexOfLastItemInAfterChange])?.ValueId;
                        value[indexOfLastItemInAfterChange] = mappingToKeep.ValueId;
                    }

                    const itemsToDelete = row.ValueId.filter(x => !value.find(y => y === x.ValueId));

                    setMappingIdsForDeletion(prev =>
                        [...prev, ...itemsToDelete
                            .filter(x => typeof x.Id !== 'string' && x.Id !== mappingToKeepId)
                            .map(x => x.Id)]
                    );

                    if (value.length === 0 || isTryingToRemoveInitialElement) {
                        setGenericFieldMappings(prev => {
                            const result = prev.filter(mapping => !itemsToDelete.find(dm => dm.Id === mapping.Id && dm.Id !== mappingToKeepId));
                            return result.map(mapping => mapping.Id !== mappingToKeepId ? mapping : { ...mapping, ValueId: newValueForMappingToKeep });
                        });
                    }
                    else {
                        setGenericFieldMappings(prev => prev.filter(mapping => !itemsToDelete.find(dm => dm.Id === mapping.Id)));
                    }
                }
                else {
                    if (row.ValueId.length === 1 && value.length === 1) {
                        editFieldWithDropDown('ValueId', value[0], row.ValueId[0].Id);
                    }
                    else {
                        const newMappingIds = value.filter(id => !row.ValueId.some(m => m.ValueId === id));
                        const newMappings = newMappingIds.map(id => ({
                            ...row,
                            Id: `newMappingId-${crypto.randomUUID()}`,
                            ValueId: id
                        }));
                        setGenericFieldMappings(prev => [...newMappings, ...prev]);
                    }

                }
            }

            return <GenericMultipleFieldValuesDropDown
                {...genericFieldValueDropDownDuplicatedProps}
                value={mappingIds}
                data={data}
                disabled={!row.FieldId || !UserHasPermission(PermissionsActions.EditFieldValue)}
                onAddItem={onAddItem}
                onChange={onChange}
                placeholder={text('ChooseFieldValue')}
                clearable
                multiple
            />
        }

        // Find the display value associated with the 'value' id, defaulting to an empty string if not found.
        const foundValue = genericFieldValues.find(x => x.Id === value)?.Value || ""

        if (!row) {
            return foundValue
        }

        // Filter the generic field values based on the FieldId of the row and ensure the Value is truthy.
        const data = genericFieldValues.filter(gfv => gfv.FieldId === row.FieldId && gfv.Value);
        return <GenericFieldValuesDropDown
            {...genericFieldValueDropDownDuplicatedProps}
            {...(foundValue && { value })}
            //In case found value is empty, don't pass it as a prop as it's not part of the dropdown's options
            data={data}
            disabled={!row.FieldId || !UserHasPermission(PermissionsActions.EditFieldValue)}
            onAddItem={async (e, { value }) => onAddGenericFieldValue(value, row)}
            onChange={(value) => onChangeGenericValue(value, row)}
            placeholder={text('ChooseFieldValue')}
            clearable
        />

    }

    function removeIndex(updatedTranslations, languageId, translationIndex) {
        const before = updatedTranslations[languageId].slice(0, translationIndex);
        const after = updatedTranslations[languageId].slice(translationIndex + 1);

        return {
            ...updatedTranslations,
            [languageId]: [...before, ...after]
        };
    }

    const createEditableTextFormatter = (property) => {
        return ({ row }) => {
            const header = text(property == 'ShortValue' ? 'ShortDescription' : 'LongDescription');
            const translationValue = derivedTranslationsForSelectedLanguage?.find(
                (t) => row?.ValueId ? t.ValueId == row?.ValueId : t.MappingId == row.Id
            )?.[property] || '';

            const saveTranslation = async (value) => {
                const updatedTranslations = await updateAndMergeTranslations(
                    genericFieldTranslations,
                    languageId,
                    row,
                    property,
                    value,
                );

                setGenericFieldTranslations(updatedTranslations);
            };

            const isDisabled = !languageId ||
                (property == 'ShortValue' ?
                    !UserHasPermission(PermissionsActions.EditShortDescription) :
                    !UserHasPermission(PermissionsActions.EditLongDescription)) ||
                multipleValuesFieldsIds?.includes(row.FieldId);

            return (
                <TextInputWithHtmlEditor
                    header={header}
                    value={translationValue}
                    disabled={isDisabled}
                    onBlur={saveTranslation}
                    maxLength={contentDescriptionLength}
                />
            );
        };
    };

    const insertManyHandler = (addedIds) => {
        setGenericFieldMappings(
            [...addedIds.map(id =>
            ({
                Id: `newMappingId-${crypto.randomUUID()}`,
                HotelChainId: currentHotelChainId,
                EntityId: entityId,
                EntityTypeId: entityTypeId,
                FieldId: id,
            })), ...genericFieldMappings])

    };


    

    const getEntityName = (Id) => {
        return entities[entityTypeId]?.find(x => x.Id == Id)?.Name || ""
    }
    const validateEntity = (EntityId, row) => {
        const duplicateValue = genericFieldMappings.find(x =>
            x.EntityTypeId === row.EntityTypeId &&
            x.EntityId === EntityId &&
            x.FieldId !== null &&
            x.FieldId === row.FieldId &&
            x.Id != row.Id);
        if (duplicateValue) {
            onWarning(message('FieldValueCannotBeDuplicate'))
            return false
        }
        return true
    }

    const entityIdPredicate = (EntityId, filter) => {
        const entityName = entities[entityTypeId].find(e => e.Id === EntityId)?.Name;
        return entityName?.toLocaleLowerCase().includes(filter.value) || null;
    };

    const fieldIdPredicate = (FieldId, filter) => {
        const fieldName = genericFieldNames.find(fn => fn.Id === FieldId)?.FieldName;
        return fieldName?.toLocaleLowerCase().includes(filter.value) || null;
    };

    const valueIdPredicate = (ValueId, filter) => {
        if (ValueId?.length) {
            return ValueId.some(gm => {
                const fieldValue = genericFieldValues.find(fv => fv.Id === gm.ValueId)?.Value;
                return fieldValue?.toLocaleLowerCase().includes(filter.value);
            }) || null;
        }

        const fieldValue = genericFieldValues.find(fv => fv.Id === ValueId)?.Value;
        return fieldValue?.toLocaleLowerCase().includes(filter.value) || null;
    };

    const textValuePredicate = (property) => {
        return (_, filter, row) => {
            const translationValue = derivedTranslationsForSelectedLanguage?.find(
                (t) => row?.ValueId ? t.ValueId == row?.ValueId : t.MappingId == row.Id
            )?.[property];
            return translationValue?.toLocaleLowerCase().includes(filter.value) || null;
        };
    }

    const gridOptions = {
        columns: [
            ...(EntityTypeId ? [] : [{
                name: 'EntityTypeId',
                title: text("EntityType"),
                formatter: ({ row }) => row ? <EntityTypesDropDown
                    {...defaultInputProps}
                    placeholder={text("EntityType")}
                    entityTypeIds={EntityTypes}
                    value={row.EntityTypeId}
                    search
                    disabled={Boolean(entityTypeId)}
                    onChange={(EntityTypeId) => {
                        if(row.EntityTypeId === EntityTypeId){
                            return;
                        }
                        row.EntityTypeId = EntityTypeId;
                        setGenericFieldMappings([...genericFieldMappings]);
                    }}
                /> : undefined,
            }]),
            ...(EntityId ? [] : [{
                name: 'EntityId',
                title: text("Entity"),
                formatter: ({ value, row }) =>
                    row ?
                        getDropDownByEntityType(
                            {
                                onChange: (EntityId) => {
                                    if (!validateEntity(EntityId, row))
                                        row.EntityId = null;
                                    else row.EntityId = EntityId
                                    setGenericFieldMappings([...genericFieldMappings]);
                                },
                                value,
                                disabled: Boolean(entityId)
                            }
                        )
                        : getEntityName(value)
            }]),
            {
                name: 'FieldId',
                title: text('FieldName'),
                formatter: editableFieldNameFormatter,
            },
            {
                name: 'ValueId',
                title: text('FieldValue'),
                formatter: editableFieldValueFormatter,
            },
            {
                name: 'ShortValue',
                title: text('ShortDescription'),
                formatter: createEditableTextFormatter('ShortValue'),
            },
            {
                name: 'LongValue',
                title: text('LongDescription'),
                formatter: createEditableTextFormatter('LongValue'),
            },
        ],
        editable: {
            allowAdd: true,
            allowDelete: UserHasPermission(PermissionsActions.DeleteContent),
            extraProps: {
                addedRows: [],
                onAddedRowsChange: addedRows => {
                    if (entityTypeId) {
                        const addedMappings = addedRows.map(createNewMapping)
                        setGenericFieldMappings([...addedMappings, ...genericFieldMappings])
                    }
                    else
                        onWarning(message('PleaseSelectAnEntityType'));
                }
            }
        },
        filtering: {
            extensions: [
                { columnName: 'EntityTypeId', filteringEnabled: false },
                {
                    columnName: 'EntityId',
                    predicate: entityIdPredicate,
                },
                {
                    columnName: 'FieldId',
                    predicate: fieldIdPredicate,
                },
                {
                    columnName: 'ValueId',
                    predicate: valueIdPredicate,
                },
                {
                    columnName: 'ShortValue',
                    predicate: textValuePredicate('ShortValue'),
                },
                {
                    columnName: 'LongValue',
                    predicate: textValuePredicate('LongValue'),
                },
            ],
        },
        columnExtensions: [
            { columnName: 'ShortValue', editingEnabled: false },
            { columnName: 'LongValue', editingEnabled: false },
        ],
        customOptions: {
            group: {
                extensions: [
                    { columnName: 'EntityTypeId', groupingEnabled: false },
                    { columnName: 'ShortValue', groupingEnabled: false },
                    { columnName: 'LongValue', groupingEnabled: false },
                ]
            }
        },
        paging: true,
        selectable: true,
        showDeleteMultiple: UserHasPermission(PermissionsActions.DeleteContent),
        cacheId: `ContentTable`,
    };

    const EntitiesDropDown = useMemo(() => () => {
        const dropDownProps = {
            style: { minWidth: 200 },
            textField: "Name",
            valueField: "Id",
            onChange: setEntityId,
            search: true,
            clearable: true,
        }
        return <DropDown data={entities[entityTypeId]} value={entityId} {...dropDownProps} />
    }, [entityTypeId, entityId, entities])

    const getDropDownByEntityType = useMemo(() => (additionalProps) => {
        const dropDownProps = {
            ...defaultInputProps,
            label: text("Entity"),
            textField: "Name",
            valueField: "Id",
            search: true,
            clearable: true,
            data: entities[entityTypeId]
        };
        return <DropDown {...dropDownProps} {...additionalProps} />
    }, [text, entities, entityTypeId]);

    const EntityTypes = useMemo(() => [
        EntityType.Rate,
        EntityType.AddOn,
        EntityType.Package,
        EntityType.PackageGroup,
        EntityType.RoomType,
        EntityType.Promotion,
        EntityType.Hotel,
        EntityType.ReservationPolicy,
        EntityType.PriceGroup,
        EntityType.ClassificationCategories,
    ], []);

    const generalSpec = useMemo(() => ({ HotelChainId: currentHotelChainId }), [currentHotelChainId]);
    const multipleValuesFieldsIds = useMemo(() =>
        genericFieldNames
            .filter(f => f.FieldType === 1)
            .map(f => f.Id),
        [genericFieldNames]
    );

    const genericFieldsData = useMemo(() => {
        const entiyFiltrationResult = genericFieldMappings.filter(x =>
            (entities[entityTypeId]?.find(entity => entity.Id === x.EntityId) && x.EntityTypeId === entityTypeId && (!entityId || x.EntityId === entityId))
            ||
            ((x.EntityId === entityId && x.EntityTypeId === entityTypeId && entityTypeId))
        )

        // Find mappings related to multiple value fields
        const multipleValuesFieldsValues = entiyFiltrationResult.filter(fm =>
            multipleValuesFieldsIds.includes(fm.FieldId)
        );

        // Group by EntityTypeId, EntityId, FieldId to get in rows in the result array of following format:
        // EntityTypeId, EntityId, FieldId - Array of values related to this field
        const multipleValuesFieldsValuesGrouped = groupByIntoArray(multipleValuesFieldsValues, 'EntityTypeId', 'EntityId', 'FieldId')
            .map((mappings) => {
                // We use last mapping as placeholder for all mapping because
                // when we add new mapping they are added as first elements
                // and we should not display them on the UI on the first row
                const indexOfLastItem = mappings.length - 1;
                const mappingToShow = mappings[indexOfLastItem];
                return {
                    Id: mappingToShow.Id,
                    FieldId: mappingToShow.FieldId,
                    EntityTypeId: mappingToShow.EntityTypeId,
                    EntityId: mappingToShow.EntityId,
                    HotelChainId: mappingToShow.HotelChainId,
                    ValueId: mappings.filter(m => Number.isInteger(m.ValueId))
                };
            });

        // For each multiple value field we should leave only one row where we will store the array 
        const result = entiyFiltrationResult.filter(fm =>
            !multipleValuesFieldsIds.includes(fm.FieldId) ||
            multipleValuesFieldsValuesGrouped.some(gfm => fm.Id === gfm.Id)
        );

        // Replace last value of last mapping with array of values
        return result.map(fm => {
            const foundGroup = multipleValuesFieldsValuesGrouped.find(gfm => fm.Id === gfm.Id)
            return foundGroup || fm;
        });
    }, [genericFieldMappings, entities, entityTypeId, entityId, multipleValuesFieldsIds]);

    return (
        <main className='content-tab'>
            <Panel style={{ flex: 0 }}>
                <Form style={{ display: 'flex', gap: '1rem', alignItems: 'baseline' }}>
                    <Form.Field
                        label={text('Language')}
                        onChange={setLanguageId}
                        clearable={true}
                        control={LanguagesDropDown}
                        value={languageId}
                        style={{ minWidth: '200px' }}
                    />
                    {!HotelId && <Form.Field
                        label={text('Hotels')}
                        spec={generalSpec}
                        onChange={(hotelIds) => setHotelIds(hotelIds)}
                        placeholder={text('All')}
                        multiple
                        value={hotelIds}
                        data={allHotelsOfChain}
                        control={DropDown}
                        textField="Name"
                        valueField="Id"
                        key="HotelIds"
                        clearable
                        style={{ minWidth: '200px' }}
                        disabled={Boolean(urlHotelId || urlEntityTypeId || urlEntityId)}
                    />}
                    {!EntityTypeId && <Form.Field
                        label={text("EntityType")}
                        control={EntityTypesDropDown}
                        style={{ minWidth: '200px' }}
                        onChange={(value) => {
                            setEntityTypeId(value);
                            setEntityId(null)
                        }}
                        entityTypeIds={EntityTypes}
                        value={entityTypeId}
                        search
                        disabled={Boolean(urlEntityTypeId)}
                    />}
                    {!EntityId && <Form.Field
                        label={text("Entity")}
                        control={EntitiesDropDown}
                        onChange={setEntityId}
                        value={entityId}
                        disabled={Boolean(urlEntityId || !entityTypeId)}
                    />}

                    <Form.Button
                        content={text('InsertMultiple')}
                        positive
                        label={<label style={{ color: "transparent" }}>.</label>}
                        onClick={() => setIsOpen(true)}
                        disabled={!(entityTypeId && entityId)}
                    />
                </Form>
            </Panel>
            <div className="gridSearchTemplate">
                <GridPanel
                    data={genericFieldsData}
                    options={gridOptions}
                    onUpdate={onGridUpdate}
                />
            </div>
            <InsertMultiplePopup
                key={isOpen}
                open={isOpen}
                closeHandler={() => setIsOpen(false)}
                insertHandler={insertManyHandler}
                genericFieldsMappings={genericFieldMappings}
                genericFieldNames={genericFieldNames}
                entityTypeId={entityTypeId}
                entityId={entityId}
            />
        </main>
    );
};

const InsertMultiplePopup = ({ closeHandler, insertHandler, open, entityTypeId, entityId, genericFieldsMappings = [] }) => {
    const [fields, setFields] = useState([]);
    const [selected, setSelected] = useState([]);

    useEffect(() => {
        if (!open) return;
        (async () => {
            const genericFieldNames = await GenericFields.GetGenericFields();
            let filteredFieldNames = genericFieldNames.filter(gfn =>
            (!genericFieldsMappings.find(x =>
                x.EntityTypeId === entityTypeId &&
                x.EntityId === entityId &&
                x.FieldId === gfn.Id) &&
                (
                    gfn.GenericFieldsMappings?.find(x => x.EntityTypeId === entityTypeId) ||
                    gfn.IsSystem
                ))).sort(genericFieldNameCompareFunction);

            setFields(filteredFieldNames);
        })();
    }, [open, genericFieldsMappings, entityTypeId, entityId]);

    const text = useTextResources();

    const insertManyHandler = () => {
        const selectedFieldIds = selected.map((id) => fields[id].Id);
        insertHandler(selectedFieldIds);
        closeHandler();
    };

    const systemGenericFieldFormatter = ({ value, row }) => {
        return row && row.IsSystem ?
            (<Popup trigger={<span>
                <span className='system-field-label-container'><Label circular color='green' empty /></span>
                <span>{value}</span>
            </span>}
                position="left center"
                basic
                content={text("SystemGenericField")}>
            </Popup>) : value;
    };

    const gridOptions = {
        columns: [
            {
                name: 'FieldName',
                title: text('FieldName'),
                formatter: systemGenericFieldFormatter
            },
        ],
        cacheId: `InsertMultiple`,
        paging: true,
        selectable: true,
        showFilter: true,
        filtering: true,
    };

    return (
        <Modal
            className='insert-multiple-modal'
            size='small'
            onClose={closeHandler}
            open={open}
            dimmer
            centered
        >
            <Modal.Header>{text('InsertMultiple')}</Modal.Header>
            <Modal.Content scrolling>
                <GridPanel options={gridOptions} onSelected={setSelected} data={fields} />
            </Modal.Content>
            <Modal.Actions>
                <Button negative onClick={closeHandler}>
                    {text('Close')}
                </Button>
                <Button positive onClick={insertManyHandler}>
                    {text('InsertMultiple')}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

InsertMultiplePopup.propTypes = {
    closeHandler: PropTypes.func.isRequired,
    insertHandler: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    entityTypeId: PropTypes.number,
    entityId: PropTypes.number,
    save: PropTypes.bool,
    genericFieldsMappings: PropTypes.array
};
